import { createClient } from '@/lib/supabase/client';

export interface Testimonial {
  id: string;
  customerName: string;
  customerRole?: string;
  content: string;
  rating: number;
  avatarUrl?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TestimonialInput {
  customerName: string;
  customerRole?: string;
  content: string;
  rating: number;
  avatarUrl?: string;
  displayOrder: number;
  isActive?: boolean;
}

class TestimonialService {
  async getAll(): Promise<Testimonial[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getActive(): Promise<Testimonial[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getById(id: string): Promise<Testimonial | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async create(input: TestimonialInput): Promise<Testimonial | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('testimonials')
      .insert([{
        customer_name: input.customerName,
        customer_role: input.customerRole,
        content: input.content,
        rating: input.rating,
        avatar_url: input.avatarUrl,
        display_order: input.displayOrder,
        is_active: input.isActive ?? true,
        created_by: user.id,
      }])
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async update(id: string, input: Partial<TestimonialInput>): Promise<Testimonial | null> {
    const supabase = createClient();
    const updateData: any = {};
    
    if (input.customerName !== undefined) updateData.customer_name = input.customerName;
    if (input.customerRole !== undefined) updateData.customer_role = input.customerRole;
    if (input.content !== undefined) updateData.content = input.content;
    if (input.rating !== undefined) updateData.rating = input.rating;
    if (input.avatarUrl !== undefined) updateData.avatar_url = input.avatarUrl;
    if (input.displayOrder !== undefined) updateData.display_order = input.displayOrder;
    if (input.isActive !== undefined) updateData.is_active = input.isActive;

    const { data, error } = await supabase
      .from('testimonials')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('testimonials')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  }

  private mapFromDb(data: any): Testimonial {
    return {
      id: data.id,
      customerName: data.customer_name,
      customerRole: data.customer_role,
      content: data.content,
      rating: data.rating,
      avatarUrl: data.avatar_url,
      displayOrder: data.display_order,
      isActive: data.is_active,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const testimonialService = new TestimonialService();